import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faPenToSquare, faShare, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import notification from "antd/es/notification";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import Folder from "@mapmycustomers/shared/types/entity/Folder";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Popconfirm } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import Path from "@app/enum/Path";
import { getCurrentUserId } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { userDisplayName } from "@app/util/formatters";

import { getResources, isResourceOrFolderSharingAllowed } from "../../store";
import { deleteFolder, emptyTrashFolder, updateFolder } from "../../store/actions";
import ShareModal from "../ShareModal";

import messages from "./messages";
import styles from "./Sidebar.module.scss";

const analyticsNames: Record<string, string> = {
  delete: "Delete",
  emptyTrash: "Empty Trash",
  rename: "Rename",
  share: "Share",
};

export interface ActionButtonProps {
  currentUserId: User["id"];
  folder?: Folder;
  hasResources: boolean;
  isTrash?: boolean;
  onDeleteFolder: typeof deleteFolder.request;
  onEmptyTrashFolder: typeof emptyTrashFolder.request;
  onSetFolder: (folder: Folder) => void;
  onUpdateFolder: typeof updateFolder.request;
  sharingAllowed: boolean;
  users: User[];
}

const ActionButton: FC<ActionButtonProps> = ({
  currentUserId,
  folder,
  hasResources,
  isTrash = false,
  onDeleteFolder,
  onEmptyTrashFolder,
  onSetFolder,
  onUpdateFolder,
  sharingAllowed,
  users,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const history = useHistory();
  const { folderId: urlFolderId } = useParams<{ folderId?: string }>();

  const [shareModal, showShareModal, hideShareModal] = useBoolean();

  const handleDelete = useCallback(() => {
    if (!folder) {
      return;
    }

    onDeleteFolder({
      id: folder.id,
      callback: () => {
        if (urlFolderId === String(folder.id)) {
          history.push(Path.RESOURCES);
        }
        notification.success({
          message: intl.formatMessage(messages.folderDelete, { folderName: folder.name }),
        });
      },
    });
  }, [folder, history, intl, onDeleteFolder, urlFolderId]);

  const handleShare = useCallback(
    (userIds: User["id"][]) => {
      if (!folder) {
        return;
      }

      onUpdateFolder({
        callback: () => {
          const sharedWith = userIds.filter((id) => currentUserId !== id);
          const user =
            sharedWith.length === 1 ? users.find(({ id }) => sharedWith.includes(id)) : undefined;

          notification.success({
            message: intl.formatMessage(messages.folderShareSuccess, {
              count: sharedWith.length,
              folderName: folder.name,
              userName: user ? userDisplayName(user) : undefined,
            }),
          });
          analytics.completed(["Share Folder"]);
          hideShareModal();
        },
        folder: {
          id: folder.id,
          user: folder.user,
          userIds,
        },
      });
    },
    [analytics, currentUserId, folder, hideShareModal, intl, onUpdateFolder, users]
  );

  const handleRename = useCallback(() => {
    if (!folder) {
      return;
    }
    onSetFolder(folder);
  }, [folder, onSetFolder]);

  const handleEmptyTrash = useCallback(() => {
    onEmptyTrashFolder();
  }, [onEmptyTrashFolder]);

  const items: MenuProps["items"] = useMemo(
    () =>
      isTrash
        ? [
            {
              disabled: !hasResources,
              key: "emptyTrash",
              label: (
                <Popconfirm
                  danger
                  description={intl.formatMessage(messages.emptyTrashMessage)}
                  disabled={!hasResources}
                  okText={intl.formatMessage(messages.deleteForever)}
                  onConfirm={handleEmptyTrash}
                  overlayClassName={styles.popconfirm}
                  placement="right"
                  trigger={["click"]}
                  zIndex={1050}
                >
                  <div className={styles.delete} onClick={stopEvents}>
                    <FontAwesomeIcon icon={faTrash} size="xs" />
                    <span>{intl.formatMessage(messages.emptyTrash)}</span>
                  </div>
                </Popconfirm>
              ),
            },
          ]
        : [
            ...(sharingAllowed
              ? [
                  {
                    icon: <FontAwesomeIcon icon={faShare} />,
                    key: "share",
                    label: intl.formatMessage(messages.share),
                    onClick: showShareModal,
                  },
                ]
              : []),
            {
              icon: <FontAwesomeIcon icon={faPenToSquare} />,
              key: "rename",
              label: intl.formatMessage(messages.rename),
              onClick: handleRename,
            },
            {
              key: "delete",
              label: (
                <Popconfirm
                  danger
                  description={intl.formatMessage(messages.deleteConfirm)}
                  okText={intl.formatMessage(messages.deleteForever)}
                  onConfirm={handleDelete}
                  overlayClassName={styles.popconfirm}
                  placement="right"
                  trigger={["click"]}
                  zIndex={1050}
                >
                  <div className={styles.delete} onClick={stopEvents}>
                    <FontAwesomeIcon icon={faTrash} size="xs" />
                    <span>{intl.formatMessage(messages.delete)}</span>
                  </div>
                </Popconfirm>
              ),
            },
          ],
    [
      handleDelete,
      handleEmptyTrash,
      handleRename,
      hasResources,
      intl,
      isTrash,
      sharingAllowed,
      showShareModal,
    ]
  );

  const handleClick: MenuProps["onClick"] = useCallback(
    (info: MenuInfo) => {
      stopEvents(info.domEvent);
      analytics.clicked(["Folder Ellipsis Menu", analyticsNames[info.key]]);
    },
    [analytics]
  );

  return (
    <>
      <Dropdown menu={{ items, onClick: handleClick }} trigger={["click"]}>
        <MoreButton onClick={stopEvents} />
      </Dropdown>
      {folder && (
        <ShareModal
          onChangeUserIds={handleShare}
          onHide={hideShareModal}
          open={shareModal}
          title={intl.formatMessage(messages.shareTitle, { name: folder.name })}
          userIds={folder.userIds}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  onDeleteFolder: deleteFolder.request,
  onEmptyTrashFolder: emptyTrashFolder.request,
  onUpdateFolder: updateFolder.request,
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  hasResources: getResources(state).length > 0,
  sharingAllowed: isResourceOrFolderSharingAllowed(state),
  users: getUsers(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActionButton);
